import { ENGINE_NAMES, type ModeId, type Outcome } from "./engine";

export const NOTHING_TO_RUN = "There is nothing to run — the editor holds no statement.";

export const RUNNING = "Running…";

export const NOT_RUN_YET = "Nothing has run yet. Execute puts everything in the editor through the database.";

export const NO_SCHEMA_YET = "The catalogue is read once the database is up.";

export const EMPTY_SCHEMA = "This database is empty. Load an example dataset, or run a CREATE of your own.";

export const NO_LOG_YET = "The database has said nothing yet.";

// A database opens empty, so a reset is the emptying and never a rebuilding: the datasets are their own control.
export const RESETTING = "Resetting the database to an empty one.";

// Said as the load begins, which is before the database it will be run against has even been opened.
export function loadingMessage(label: string): string {
  return `Loading the ${label} dataset: its script is in the editor, and runs as the database comes up.`;
}

// What a load is about to take away: the editor it writes its script into, and the tables it opens a fresh database
// over. A session with nothing in that database is only being asked about the first of those, so it is only told it.
export function loadWarning(label: string, populated: boolean): string {
  const database = populated ? ", so the tables in this one go with it" : "";
  return `Loading the ${label} dataset writes its script into the editor and runs it against a fresh database${database}.`;
}

// The half of that which can be got back, since only one of the two can and it is worth saying which.
export const LOAD_UNDONE = "The editor's own undo still has what was there. Nothing here brings a database back.";

// A dataset is run the moment its database is up, so what somebody is waiting for is both of those and not one.
export function startingMessage(mode: ModeId, dataset: string | null): string {
  const engine = ENGINE_NAMES[mode];
  return dataset ? `Starting ${engine} and loading the ${dataset} dataset…` : `Starting ${engine}…`;
}

// Every database opens empty, dataset or no dataset: what fills one is the script above, run a statement at a time.
export function readyMessage(version: string): string {
  return `${version} is up, with an empty database.`;
}

export function startFailure(mode: ModeId, reason: string): string {
  return `${ENGINE_NAMES[mode]} did not start: ${reason}`;
}

// The tail of a statement's log line: what the database called it, what it answered with, and how long it took.
export function summarise(outcome: Outcome, ms: number): string {
  const parts = [outcome.command ?? ""];
  if (outcome.columns.length > 0) parts.push(count(outcome.rows.length, "row"));
  if (outcome.affected !== null) parts.push(`${count(outcome.affected, "row")} affected`);
  return `${parts.filter(Boolean).join(", ")} in ${writeMillis(ms)}`;
}

// What the results tab says when a run finished with no result set to draw, which a script of writes always does.
export function ranMessage(done: number, ms: number): string {
  return `${count(done, "statement")} ran in ${writeMillis(ms)}. None of them returned rows.`;
}

// Said above the grid when the answer was longer than the grid will draw, so the two numbers are never confused.
export function truncatedMessage(shown: number, total: number): string {
  return `Showing the first ${shown.toLocaleString()} of ${total.toLocaleString()} rows.`;
}

export function schemaFailure(reason: string): string {
  return `The catalogue could not be read: ${reason}`;
}

// Sub-millisecond down to a hundredth, since a prepared SELECT over seven rows is honestly that fast and a flat "0 ms"
// reads as a measurement that was never taken.
export function writeMillis(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return ms >= 10 ? `${Math.round(ms)} ms` : `${ms.toFixed(2)} ms`;
}

export function count(total: number, noun: string): string {
  return `${total} ${noun}${total === 1 ? "" : "s"}`;
}
